import { useMutation, useQueryClient } from '@tanstack/react-query'

import api from '@/lib/api'
import { useAccessToken, useCurrentUser } from '@/features/auth/state/auth'

type PrimitiveField = string | number | boolean

export interface EnviarPrevidenciarioZipRequest {
  idPrevidenciario: string
  evento?: string
  certFile?: File
  caFile?: File
  employerTpInsc?: string
  employerNrInsc?: string
  senderTpInsc?: string
  senderNrInsc?: string
  target?: string
  groupId?: number
  genEventId?: boolean
  forceLayoutVersion?: string
  gerarFechamentos?: boolean
  forceIndretif1?: boolean
  useFullNrInsc?: boolean
  certPassword?: string
}

export interface EnviarPrevidenciarioZipResponse {
  [key: string]: unknown
}

function appendField(formData: FormData, key: string, value?: PrimitiveField) {
  if (value === undefined || value === null) return
  formData.append(key, String(value))
}

export function useEnviarPrevidenciarioZip() {
  const queryClient = useQueryClient()
  const token = useAccessToken()
  const user = useCurrentUser()

  return useMutation({
    mutationFn: async (request: EnviarPrevidenciarioZipRequest): Promise<EnviarPrevidenciarioZipResponse> => {
      if (!request.idPrevidenciario?.trim()) {
        throw new Error('idPrevidenciario é obrigatório')
      }
      if (!request.certFile) {
        throw new Error('Certificado A1 (.pfx) é obrigatório')
      }
      if (!request.certPassword?.trim()) {
        throw new Error('Senha do certificado é obrigatória')
      }

      if (!user || !token) {
        throw new Error('Usuário não autenticado')
      }

      const formData = new FormData()
      formData.append('id_previdenciario', request.idPrevidenciario)
      if (request.certFile) formData.append('cert_file', request.certFile)
      if (request.caFile) formData.append('ca_file', request.caFile)

      appendField(formData, 'evento', request.evento)
      appendField(formData, 'employer_tp_insc', request.employerTpInsc)
      appendField(formData, 'employer_nr_insc', request.employerNrInsc)
      appendField(formData, 'sender_tp_insc', request.senderTpInsc)
      appendField(formData, 'sender_nr_insc', request.senderNrInsc)
      appendField(formData, 'target', request.target ?? 'prod')
      appendField(formData, 'group_id', request.groupId)
      appendField(formData, 'gen_event_id', request.genEventId ?? true)
      appendField(formData, 'force_layout_version', request.forceLayoutVersion)
      appendField(formData, 'gerar_fechamentos', request.gerarFechamentos)
      appendField(formData, 'force_indretif_1', request.forceIndretif1)
      appendField(formData, 'use_full_nr_insc', request.useFullNrInsc)
      appendField(formData, 'cert_password', request.certPassword)

      const response = await api.post<EnviarPrevidenciarioZipResponse>('/previdenciario/enviar_zip', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      })

      if (!response.data) {
        throw new Error('Erro ao enviar ZIP previdenciário')
      }

      return response.data
    },
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({ queryKey: ['previdenciario-detalhes', request.idPrevidenciario] })
      queryClient.invalidateQueries({ queryKey: ['esocial-retornos', request.idPrevidenciario] })
    }
  })
}
